// This file contains the Schema used to configure a scenario in the firebase database
// All firebase scenarios with isVersion2 = true should adhere to this schema
// The very same schema should be used in the Virtex Application
//
// Note: Do not use optional properties
// -----------------------------------------------------------------------------
// Scenario (top-level container)
// -----------------------------------------------------------------------------
// Renamed from Scenario to ScenarioModel to avoid name conflicts in multi-user Virtex exercise
export class ScenarioModel {
  // Names/config
  id: string = '-1';
  seqNo: number = 100;

  displayName: string = 'Default Scenario';
  description: string = 'Describe your scenario here';
  institute: string = 'Default Institute';
  owner: string = 'owner'
  status: string = "In dev"

  enablePanelPad: boolean = false; // whether to enable the panel pad that moves the player  
  cameraConfigName: string = 'default'; // Default camera configuration


  // Flags and description
  isVersion2: boolean = true;
  forceAssetRefresh: boolean = false; // when true, forces the client to refresh all assets (e.g. after an update)
  doPerformanceLogging: boolean = false;
  showAxis: boolean = false;
  doLogAssetSizes: boolean = true; // when true, the size of the glb files will be logged in firebase
  hasInventory: boolean = false; 

  // World setup
  ground: GroundModel = new GroundModel();
  lighting: LightingModel = new LightingModel();
  fog: FogModel = new FogModel();
  skybox: SkyboxModel = new SkyboxModel();
  stagingX0: number = -5;
  stagingX1: number = 5;
  stagingY0: number = -5;
  stagingY1: number = 5;
  angle0: number = 0; // initial angle of the player in degrees, 
  // 0 means facing towards positive z direction

  // Collections
  components: ComponentModel[] = [];
  interactivities: InteractivityModel[] = [];
  lessonplan: LessonPlanStepModel[] = [];
  modifiers: ModifierModel[] = [];
}

// LightingModel --------------------------------------------------------------
export class LightingModel {
  methodName: string = 'createStandardLighting'; // white
}

// FogModel -------------------------------------------------------------------
export class FogModel {
  methodName: string = 'createStandardFog'; // white
  fogColor: string = 'mistyWhite'; // default color
}

// -----------------------------------------------------------------------------
// Ground & Skybox (world primitives)
// -----------------------------------------------------------------------------
// Renamed from Ground to GroundModel
export class GroundModel {
  methodName: string = 'createStandardGround';
  width: number = 60;
  height: number = 60;
  subdivisions: number = 20;

  fileName: string = 'ground.png'; //diffuse file
  terrain: string = '';
  // maxHeight appears as an empty string sometimes; allow both
  maxHeight: number;

  // other standard material files
  normalFileName: string = ''; //normal file (use png) ; not used if empty

  // other PBR matierial files
  roughnessFileName: string = ''; // roughness file (use png) ; not used if empty
  displacementFileName: string = ''; // displacement file (use png) ; not used if empty  
  extensionYOffset: number = -0.05
}

// Renamed from Skybox to SkyboxModel
export class SkyboxModel {
  posy: number = 0;
  reflectionTexture: string = 'arid';
  size: number = 100;
}

// -----------------------------------------------------------------------------
// Components (3D models placed in the world)
// -----------------------------------------------------------------------------
export class ComponentModel {
  id: string = "";
  name: string = "Default Component";
  type: string = "ComponentModel";
  file: string = "";
  enabled: boolean = true;
  isPickable: boolean = false;
  checkCollisions: boolean = false;
  isVisible: boolean = true;
  isWalkable: boolean = false; // if true, the player can walk on this component; otherwise, it will be treated as an obstacle

  // transform
  posX: number = 0;
  posY: number = 0;
  posZ: number = 0;
  rotX: number = 0;
  rotY: number = 0;
  rotZ: number = 0;
  scale: number = 1;
}

// -----------------------------------------------------------------------------
// Interactivities (base class)
// -----------------------------------------------------------------------------
export class InteractivityModel {
  class: string = 'Interactivity';
  enabled: boolean = true;
  component: string = '';
  name: string = '';
  seqNo: number = 100;
  // domid connects lessonplan steps, interactivities and panelservice
  domid: string = ''; // the dom id that can be used by lessonplanhandler, interactivity and panelservice
  isTeacherControlled: boolean = false; // if true, this interactivity can only be triggered by the teacher through the lesson plan; otherwise, it can be triggered by students as well
}

// -----------------------------------------------------------------------------
// InteractivityModel Extensions
// -----------------------------------------------------------------------------
export class Approach extends InteractivityModel {
  maxNumberOfSteps: number = 5;
  penaltySteps: number = 15 // [seconds]: penalty if max steps exceeded
  minWaitForSensor: number = 20 // [seconds]: min wait time before sensor can be viewed
  penaltyWaitForSensor: number = 10 // [seconds]: penalty for viewing sensor before min wait time
}

export class ArecModel extends InteractivityModel {
  // nothing for now
}

export class BoilerModel extends InteractivityModel {
  domid_vent: string = '';
}

export class CompassModel extends InteractivityModel {
  override class: string = 'Compass';
}

export class ExploreArecModel extends InteractivityModel {
  // nothing for now
}

export class ExploreMapModel extends InteractivityModel {
  // nothing for now
}

// Renamed from Fire to FireModel to avoid name conflicts
export class FireModel extends InteractivityModel {
  posX: number = 0;
  posY: number = 0;
  posZ: number = 0;
}

export class GateOpenCloseModel extends InteractivityModel {
  open: string[] = ['openleft', 'openright'];
  close: string[] = ['', ''];
}

// Show be moved to modifiers
export class GrassModel extends InteractivityModel {
  nBlades: number = 0
  radius: number = 0
  xPos: number = 0
  zPos: number = 0
}

export class InviteModel extends InteractivityModel {
  // nothing for now
}

export class LabelDisplayModel extends InteractivityModel {
  htmlFileName: string = ''
}

export class HyperlinkModel extends InteractivityModel {
  hyperlink: string = ''
}
export class MapModel extends InteractivityModel {
  viewMode: string = 'full'; // can be 'full' or 'insert' or 'modal'
  direction: string = 'N'; // can be 'N;,'E','S','W'; default is 'N'
}

export class ManholeModel extends InteractivityModel {
  animation: string = 'explode';
}

export class ModalDisplayModel extends InteractivityModel {
  htmlFileName: string = 'modal.html';
  modalid: string = 'modal1'; //the close button is appended by "close"
}

export class NavigationModel extends InteractivityModel {
  // nothing for now
}

export class PhaseInOutModel extends InteractivityModel {
  duration: number = 3; // duration of the phase in/out effect in seconds
}

export class PlayAnimationModel extends InteractivityModel {
  animation: string = 'sewerexplosion'
}

export class PlayAudioModel extends InteractivityModel {
  audiofile: string = 'daycare'
  title: string = 'Audio Title'
  description: string = 'Audio Description'
}

export class PPEModel extends InteractivityModel {
  override class: string = 'PPE'
  ppefiles: string[] = ['workerlevela', 'workerlevelb', 'workerlevelc', 'workerleveld']
}

export class RolesModel extends InteractivityModel {
  override class: string = 'Roles'
  roleNames: string[] = ['Commander', 'Entry', 'Observer'];
}

export class RotateModel extends InteractivityModel {
  override class: string = 'Rotate';
  rotAxis: string = 'y'; // can be 'x', 'y' or 'z'
  rotAngle: number = 90; // in degrees
  duration: number = 2; // in seconds
  tooltip: string = 'Rotate'; // tooltip to show when hovering over the component; can be set in the scenario or left as default  
  toastOn: string = 'Rotation complete'; // toast message to show when the rotation is complete; can be set in the scenario or left as default
  toastOff: string = ''; // toast message to show when the rotation is turned off (if toggle is used); can be set in the scenario or left as default  
}

export class ScenarioDescriptionModel extends InteractivityModel {
  htmlFileName: string = ''
  // the html file that contains the description of the scenario
}

export class SickCharModel extends InteractivityModel {
  scenario: string = ''
  animmation: string = ''
}

export class SensorSimModel extends InteractivityModel {
  override class: string = 'SensorSim';
  // just defaults for now
  components: { componentName: string, sensor: string, levelAt1: number }[] = [
    { componentName: 'dumpster', sensor: 'CO', levelAt1: 20 },
    { componentName: 'dumpster', sensor: 'H2S', levelAt1: 23 },
    { componentName: 'hydrant', sensor: 'LEL', levelAt1: 15 },
  ];
}

export class SewerModel extends InteractivityModel {
  domidFlush: string = '';
  domidExplosion:string = '';
  domidVentilate: string = '';
}

// Renamed from Fire to FireModel to avoid name conflicts
export class SmokeModel extends InteractivityModel {
  type: string = 'default'; // can be used to differentiate different types of smoke (e.g. light, medium, heavy)
  posX: number = 0;
  posY: number = 0;
  posZ: number = 0;
}

export class TankertruckModel extends InteractivityModel {
  doorMesh: string = 'door'
  documentation: string = 'document'
}

// Renamed from Tracker to TrackerModel
export class TrackerModel extends InteractivityModel {
  // No additional fields yet
}

export class WeatherStationModel extends InteractivityModel {
  temperature: number = 20; // in degree Celsius
  humidity: number = 50; // in percentage
  pressure: number = 1013; // in hPa
  windspeed: number = 10; // in km/h
  winddirection: string = 'N'; // can be N, NE, E, SE, S, SW, W, NW
  rainfall: number = 0; // in mm
}


// Renamed from WindDirection to WindDirectionModel
export class WindDirectionModel extends InteractivityModel {
  // No additional fields yet
}

export class WindHoseModel extends InteractivityModel {
  // nothing for now
}
// -----------------------------------------------------------------------------
// Lesson Plan (base class)
// -----------------------------------------------------------------------------
// Renamed from LessonPlanStep to LessonPlanStepModel
export class LessonPlanStepModel {
  class: string = 'LessonPlanStep';
  id: string = '';
  domid: string = ''; // the dom id that can be used by lessonplanhandler, interactivity and panelservice
  name: string = '';
  description: string = '';
  seqNo: number = 0;
  panelSectionId: string = '';
}

// -----------------------------------------------------------------------------
// LessonPlanStepModel Extensions
// -----------------------------------------------------------------------------
// Renamed from LessonPlanStepUI to LessonPlanStepUIModel
export class LessonPlanStepUIModel extends LessonPlanStepModel {
  override class: string = 'LessonPlanStepUI';
  buttonText: string = '';
  interactivityClass: string = '';
  modalId: string = '';
}

// -----------------------------------------------------------------------------
// Modifiers (base class)
// -----------------------------------------------------------------------------
// Renamed from Modifier to ModifierModel
export class ModifierModel {
  class: string = 'Interactivity';
  enabled: boolean = true;
  component: string = '';
  name: string = '';
  seqNo: number = 100;
}

// -----------------------------------------------------------------------------
// ModifierModel Extensions
// -----------------------------------------------------------------------------
export class ThermometerModel extends ModifierModel {
}

export class PathInstancerModel extends ModifierModel {
  commands: string = '';
  forewardAxis: string = '+X'; // can be +X, -X, +Z, -Z; determines the forward direction of the instanced objects; default is +X
  stepSize: number = 2.55; // the distance between each instance; default is 2.55 (which is the size of the floor tiles in the standard ground)
  originOffsetX: number = 0; // offset of the first instance in X direction; default is 0
  originOffsetZ: number = 0; // offset of the first instance in Z direction; default is 0
  rotationOffsetDeg: number = 0; // rotation offset of the instanced objects in degrees; default is 0
}

export class WaterModel extends ModifierModel {
  waterMethod: string = 'creek'
}

// -----------------------------------------------------------------------------
// Component Copies
// -----------------------------------------------------------------------------
export class ComponentCopyModel extends ModifierModel {
  override class: string = 'ComponentCopy';
  copies: { name: string, posX: number, posY: number, posZ: number, rotX: number, rotY: number, rotZ: number, scale: number}[] = [
    { name: 'Copy1', posX: 10, posY: 0, posZ: 5, rotX: 0, rotY: 90, rotZ: 0 ,scale:1},
    { name: 'Copy2', posX: -10, posY: 0, posZ: -5, rotX: 0, rotY: 180, rotZ: 0 ,scale:1}
  ];
}


